import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getMembers, getMembersData } from '../../actions/dashboard/members_index'
import { deleteMember, showMemberFormPopupEditGolfer } from '../../actions/dashboard/members_index'
import PropTypes from 'prop-types'
import MemberDeletePopup from './member_delete_popup'
import { GGMenu } from '../../../shared/new_components/GGMenu'
import InfiniteScrollTable from '../../../shared/components/infinite_scroll_table'


const MembersTableComponent = ({ searchInput, width }) => {
  const dispatch = useDispatch()
  const members = useSelector(state => state.members)
  const needRefresh = useSelector(state => state.needRefresh)
  const membersLoaded = useSelector(state => state.membersLoaded)
  const isLoading = useSelector(state => state.isLoading)
  const totalMembers = useSelector(state => state.totalMembers)

  const tableRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(0)

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (tableRef.current) {
        const top = tableRef.current.getBoundingClientRect().top
        const saveBar = document.querySelector('.save_bar_light')
        const saveBarHeight = saveBar ? saveBar.offsetHeight + 20 : 0
        setTableHeight(window.innerHeight - top - saveBarHeight)
      }
    }
    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [width])

  const [isOpenDeletePopup, setIsOpenDeletePopup] = useState(false)
  const [deleteMemberId, setMemberDeleteId] = useState(null)
  const [deleteText, setDeleteText] = useState('')

  const initialSortOrder = 'asc'
  const initialSortParam = 'Entry Number'

  const fetchData = (sortParam, sortOrder, stopIndex) => {
    dispatch(getMembers(sortParam, sortOrder, stopIndex, searchInput, stopIndex === 0))
  }

  const handleDelete = (memberId, deleteText) => {
    setIsOpenDeletePopup(true)
    setMemberDeleteId(memberId)
    setDeleteText(deleteText)
  }
  const handleEdit = (memberId) => {
    dispatch(showMemberFormPopupEditGolfer(memberId))
  }

  useEffect(() => {
    dispatch(getMembersData())
  }, [needRefresh])

  const memberTableColumns = [
    {
      dataKey: 'action',
      width: 100,
      cellRenderer: ({ rowData }) => {
        const deleteText = window.I18n.t('gg_golf_hub.members_table.delete_confirmation', {
          first_name: rowData.firstName,
          last_name: rowData.lastName,
        })
        const menuOptions = [
          { label: window.I18n.t('buttons.edit'), value: 'edit', icon: 'pen-o', usePro: true },
          { label: window.I18n.t('buttons.delete'), value: 'delete', icon: 'trash-can-o', danger: true, usePro: true },
        ]
        const handleActionSelect = (value) => {
          if (value === 'edit') {
            handleEdit(rowData.id)
          } else if (value === 'delete') {
            handleDelete(rowData.id, deleteText)
          }
        }
        return (<GGMenu options={menuOptions} onSelect={handleActionSelect} position='right' />)
      },
    },
    {
      dataKey: 'entryNumber',
      width: 120,
      isSortable: true,
      sortKey: 'Entry Number',
      columnTitle: window.I18n.t('gg_golf_hub.members_table.columns.entry_number'),
    },
    {
      dataKey: 'teamId',
      width: 130,
      isSortable: true,
      sortKey: 'Team Id',
      columnTitle: window.I18n.t('gg_golf_hub.members_table.columns.team_id'),
    },
    {
      dataKey: 'lastName',
      width: 160,
      isSortable: true,
      sortKey: 'last_name',
      columnTitle: window.I18n.t('gg_golf_hub.member_form_popup.last_name'),
      className: 'orange',
      cellRenderer: ({ rowData }) => {
        return (
          <a href='' onClick={(e) => {
            e.preventDefault()
            handleEdit(rowData.id)
          }}>{rowData.lastName}</a>
        )
      },
    },
    {
      dataKey: 'firstName',
      width: 180,
      isSortable: true,
      sortKey: 'first_name',
      columnTitle: window.I18n.t('gg_golf_hub.member_form_popup.first_name'),
    },
    {
      dataKey: 'gender',
      width: 120,
      isSortable: true,
      sortKey: 'Gender',
      columnTitle: window.I18n.t('gg_golf_hub.member_form_popup.gender'),
    },
    {
      dataKey: 'email',
      width: 240,
      isSortable: true,
      sortKey: 'email',
      columnTitle: window.I18n.t('gg_golf_hub.members_table.columns.email'),
    },
    {
      dataKey: 'phone',
      width: 240,
      isSortable: true,
      sortKey: 'Phone Number',
      columnTitle: window.I18n.t('gg_golf_hub.members_table.columns.phone'),
    },
    {
      dataKey: 'handicapId',
      width: 240,
      isSortable: true,
      sortKey: 'automated_handicaps',
      columnTitle: window.I18n.t('gg_golf_hub.member_form_popup.handicap_id'),
    },
  ]
  
  const minTableWidth = memberTableColumns.reduce((sum, col) => sum + col.width, 0)

  return (
    <div className='members-table-component' ref={tableRef}>
      <MemberDeletePopup isOpen={isOpenDeletePopup} title={deleteText}
        onClose={() => setIsOpenDeletePopup(false)}
        onDelete={() => {
          setIsOpenDeletePopup(false)
          dispatch(deleteMember(deleteMemberId))
        }} />
      <div style={{ display: membersLoaded && members.length > 0 ? 'block' : 'none', height: tableHeight }}>
        <div style={{ minWidth: minTableWidth, height: '100%' }}> 
          <InfiniteScrollTable
            list={members}
            isLoading={isLoading}
            totalListCount={totalMembers}
            tableColumns={memberTableColumns}
            fetchData={fetchData}
            initialSortOrder={initialSortOrder}
            initialSortParam={initialSortParam}
            dependencyArray={[searchInput, needRefresh]}
          />
        </div>
      </div>
      {totalMembers === 0 && searchInput === '' && <div className='no-golfers'>{window.I18n.t('gg_golf_hub.members_table.no_golfers_yet')}</div>}
      {membersLoaded && members.length === 0 && searchInput !== '' && <div className='no-golfers'>{window.I18n.t('gg_golf_hub.members_table.no_golfers')}</div>}
    </div>
  )
}

MembersTableComponent.propTypes = {
  searchInput: PropTypes.string,
  width: PropTypes.number,
}

export default MembersTableComponent
